/**
 * usePublicSharePage
 *
 * Shared orchestration for the public shared-map pages (`SharedMapPage` and
 * `EmbedMapPage`). Bundles the data hook (slim LIST + MAP), store hydration,
 * URL-driven selection, lazy per-artifact DETAIL fetch, and artifact
 * navigation, then exposes the share status, the ready-state values, and the
 * stable `handleSelect` / `handleSubmitPassword` handlers.
 *
 * The three early-return states (loading / fatal status / password gate) are
 * driven off `status` + `passwordError`; pages hand those to `PublicShareGate`
 * and `return` it before rendering their own layout. Pages keep only their
 * genuine differences — the optional `onAfterSelect` side effect (e.g. closing
 * the mobile list sheet) is parameterized here.
 *
 * @module components/share/public/usePublicSharePage
 */
import { useCallback } from 'react';

import { useArtifactNavigation } from '@/hooks/useArtifactNavigation';
import { useDeviceLayout } from '@/hooks';
import { useGeoMarkupStore } from '@/store';
import { selectMapNotes } from '@/store/selectors';
import { selectMapTasks } from '@/store/selectors/taskSelectors';
import { selectObstructionsMapItems } from '@/store/selectors/obstructionSelectors';
import { previewZoomBottomPadding } from '@/utils/zoomHelpers';
import type { Coordinates } from '@/types';
import type {
  PublicShareConfig,
  PublicShareListData,
  PublicShareMapData,
  ShareViewerFilters,
} from '@/types/share';

import {
  usePublicArtifactDetail,
  type PublicArtifactDetailSlot,
} from './usePublicArtifactDetail';
import {
  usePublicArtifactSelection,
  kindToArtifactId,
  type PublicArtifactDescriptor,
} from './usePublicArtifactSelection';
import { usePublicShare, type PublicShareStatus } from './usePublicShare';
import { usePublicShareHydration } from './usePublicShareHydration';

/**
 * Resolves an artifact's anchor coordinates from the hydrated MAP-tier datasets
 * (the slim list descriptor carries no coordinates). Matches by `shortId` with
 * an `id` fallback. Returns `null` when the artifact is not in the map data
 * (e.g. hidden by a viewer filter, or no location). Pure over its inputs so
 * both the list/map click path and the detail-zoom effect can drive it.
 */
export function resolveDescriptorCoordinates(
  descriptor: PublicArtifactDescriptor,
  mapTasks: ReturnType<typeof selectMapTasks>,
  mapNotes: ReturnType<typeof selectMapNotes>,
  mapObstructions: ReturnType<typeof selectObstructionsMapItems>
): Coordinates | null {
  const matches = (item: { id: string; shortId: string }): boolean =>
    item.shortId === descriptor.shortId || item.id === descriptor.id;

  switch (descriptor.kind) {
    case 'task': {
      const task = mapTasks.find(matches);
      return task?.anchorPoint ?? null;
    }
    case 'note': {
      const note = mapNotes.find(matches);
      return note ? { lat: note.latitude, lng: note.longitude } : null;
    }
    case 'obstruction': {
      const obstruction = mapObstructions.find(matches);
      return obstruction?.anchorPoint ?? null;
    }
  }
}

interface UsePublicSharePageOptions {
  /** Extra side effect run after a list/map selection (e.g. close mobile sheet). */
  onAfterSelect?: () => void;
}

interface UsePublicSharePageResult {
  status: PublicShareStatus;
  passwordError: boolean;
  rateLimited: boolean;
  /** Non-null only once `status === 'ready'`. */
  config: PublicShareConfig | null;
  /** Non-null only once `status === 'ready'`. */
  listData: PublicShareListData | null;
  /** Non-null only once `status === 'ready'`; consumed by store hydration. */
  mapData: PublicShareMapData | null;
  /** URL-resolved reference to the open artifact; null when closed. */
  descriptor: PublicArtifactDescriptor | null;
  /** Lazily-loaded kind + full DTO for the open artifact; null while loading/closed. */
  artifact: PublicArtifactDetailSlot | null;
  isDetailLoading: boolean;
  detailError: boolean;
  viewerFilters: ShareViewerFilters;
  setViewerFilters: (filters: ShareViewerFilters) => void;
  handleSelect: (next: PublicArtifactDescriptor) => void;
  handleSubmitPassword: (password: string) => void;
  closeOverlay: () => void;
}

export function usePublicSharePage(
  token: string,
  options: UsePublicSharePageOptions = {}
): UsePublicSharePageResult {
  const { onAfterSelect } = options;

  const {
    status,
    config,
    listData,
    mapData,
    viewerFilters,
    passwordError,
    rateLimited,
    setViewerFilters,
    submitPassword,
  } = usePublicShare(token);

  usePublicShareHydration(token, config, mapData);
  const descriptor = usePublicArtifactSelection(listData);
  const { artifact, isDetailLoading, detailError } = usePublicArtifactDetail(
    token,
    descriptor
  );
  const { openArtifact, closeOverlay } = useArtifactNavigation();
  const { isMobile } = useDeviceLayout();
  const zoomTo = useGeoMarkupStore((state) => state.map.zoomTo);

  // Mirror the authenticated list navigation (`navigateFromList`): selecting an
  // artifact opens its detail AND flies the camera to it. The slim descriptor
  // has no coordinates, so resolve them from the hydrated MAP store. On mobile
  // the bottomPadding frames the pin above the preview sheet.
  const handleSelect = useCallback(
    (next: PublicArtifactDescriptor) => {
      openArtifact(kindToArtifactId(next.kind), 'detail', next.shortId);
      const state = useGeoMarkupStore.getState();
      const coordinates = resolveDescriptorCoordinates(
        next,
        selectMapTasks(state),
        selectMapNotes(state),
        selectObstructionsMapItems(state)
      );
      if (coordinates) {
        zoomTo(coordinates, {
          bottomPadding: previewZoomBottomPadding(isMobile),
        });
      }
      onAfterSelect?.();
    },
    [openArtifact, zoomTo, isMobile, onAfterSelect]
  );

  const handleSubmitPassword = useCallback(
    (password: string) => {
      void submitPassword(password);
    },
    [submitPassword]
  );

  return {
    status,
    passwordError,
    rateLimited,
    config,
    listData,
    mapData,
    descriptor,
    artifact,
    isDetailLoading,
    detailError,
    viewerFilters,
    setViewerFilters,
    handleSelect,
    handleSubmitPassword,
    closeOverlay,
  };
}
